import React from "react";

import {
  COMMERCIAL_CATEGORIES,
  upgradeTargetFor,
  userIsRestricted,
} from "../../../../hooks/useCommercialAccess";

/**
 * The property-type checkbox list, rendered identically on every search
 * surface.
 *
 * It used to be hand-rolled four times — twice in Filters-search.jsx (the
 * legacy surface behind /search-result, /property-details, /owners,
 * /owner-property, /owner-overview) and twice in new-filters-form.jsx
 * (/search-property) — and the four copies had drifted apart: only the new
 * form grouped Residential/Commercial or honoured the commercial gate, and
 * the legacy More-filters tab didn't even sort the list or drop the
 * Miscellaneous bucket. This is now the only place the list is built.
 *
 * The two surfaces still drive their forms differently, so selection is
 * passed in rather than owned here:
 *
 *   - react-hook-form (new-filters-form) — controlled; pass `filterData`
 *     (or `selectedValues`) and let the default `setValue` handler run.
 *   - the legacy DOM-read forms (Filters-search) — `uncontrolled`, because
 *     handleSubmit() reads `checked` straight off the inputs inside the
 *     <form>. Pass `selectedValues` for the initial state and `onChange`.
 */

const PRIORITY_ORDER = [
  "residential",
  "commercial",
  "single family",
  "seasonal; cabin; vacation residence",
  "church",
];

const COMMERCIAL_CATEGORIES_LOWER = COMMERCIAL_CATEGORIES.map((category) =>
  category.toLowerCase()
);

/**
 * Lay one group out down two columns, then read it back row-major so the
 * grid renders it in column order.
 *
 * Deliberately per-group: doing it once across both groups made the
 * residential order depend on how many commercial categories were in the
 * list, so the residential half visibly reshuffled the moment the plan
 * landed and the commercial half went away.
 */
function layoutColumns(entries) {
  const priorityItems = [];
  const otherItems = [];

  entries.forEach((item) => {
    const [key] = item;
    if (PRIORITY_ORDER.includes(key.toLowerCase())) {
      priorityItems.push(item);
    } else {
      otherItems.push(item);
    }
  });

  // Sort priority items by predefined order
  priorityItems.sort(
    ([keyA], [keyB]) =>
      PRIORITY_ORDER.indexOf(keyA.toLowerCase()) -
      PRIORITY_ORDER.indexOf(keyB.toLowerCase())
  );

  // Sort other items alphabetically
  otherItems.sort(([keyA], [keyB]) =>
    keyA.toLowerCase().localeCompare(keyB.toLowerCase())
  );

  // Left column starts with the priority items, then fills with the rest
  const itemsPerColumn = Math.ceil((priorityItems.length + otherItems.length) / 2);
  const leftColItems = [...priorityItems];
  const remainingSlotsLeft = itemsPerColumn - leftColItems.length;

  if (remainingSlotsLeft > 0) {
    leftColItems.push(...otherItems.slice(0, remainingSlotsLeft));
  }

  const rightColItems = otherItems.slice(Math.max(0, remainingSlotsLeft));

  // Interleave for row-major rendering (left, right, left, right...)
  const laidOut = [];
  const maxRows = Math.max(leftColItems.length, rightColItems.length);

  for (let i = 0; i < maxRows; i++) {
    if (i < leftColItems.length) {
      laidOut.push(leftColItems[i]);
    }
    if (i < rightColItems.length) {
      laidOut.push(rightColItems[i]);
    }
  }

  return laidOut;
}

/**
 * The ordered Residential/Commercial partition a customer sees.
 *
 * Three states, not two. memData arrives on an authenticated POST to
 * site-settings; the counts arrive on an unauthenticated GET that CloudFront
 * edge-caches, so the counts win that race on every page load and this runs
 * at least once with the plan still unknown.
 *
 * "Unknown" must not render as restricted — a Legend customer would watch a
 * locked state flash at them. But it must not render as entitled either:
 * that made a restricted customer watch the whole commercial half appear and
 * then vanish, which is the worse half of the trade. So the commercial group
 * waits until the plan is actually known, and only residential — which is
 * the same for everyone — renders immediately.
 *
 * Exported so the ordering and the gate can be asserted without a render.
 */
export function groupPropertyTypes(propertyCatsCounts, memData) {
  const counts = propertyCatsCounts || {};

  // The reducer starts memData at null and resets it to null on every
  // fetch, so null/undefined is exactly "site-settings has not answered yet".
  const planKnown = memData != null;
  const isRestricted = userIsRestricted(memData);

  const residentialEntries = [];
  const commercialEntries = [];

  Object.entries(counts).forEach((entry) => {
    const keyLower = String(entry[0]).toLowerCase();

    if (keyLower === "miscellaneous") {
      return;
    }
    // Check if this category is in the commercial categories list (exact match)
    if (COMMERCIAL_CATEGORIES_LOWER.includes(keyLower)) {
      commercialEntries.push(entry);
    } else {
      residentialEntries.push(entry);
    }
  });

  // Measured against every commercial category the area has, not against what
  // survived the gate — for a restricted customer that list is always empty
  // by this point, which is what made the upgrade notice unreachable.
  const hasHiddenCommercial = isRestricted && commercialEntries.length > 0;

  return {
    residential: layoutColumns(residentialEntries),
    commercial:
      planKnown && !isRestricted ? layoutColumns(commercialEntries) : [],
    planKnown,
    isRestricted,
    hasHiddenCommercial,
  };
}

const GROUP_HEADING_STYLE = {
  fontSize: "12px",
  fontWeight: "600",
  color: "#666",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
};

export default function PropertyTypesOptions({
  fieldNamePrefix = "",
  idPrefix,
  setValue,
  filterData,
  selectedValues,
  onChange,
  uncontrolled = false,
  propertyCatsCounts,
  memData = null,
  // Accepted so the react-hook-form call sites can keep passing their whole
  // form bag; the list itself needs neither.
  register, // eslint-disable-line no-unused-vars
  watch, // eslint-disable-line no-unused-vars
}) {
  const fieldName = fieldNamePrefix
    ? `${fieldNamePrefix}_property_type`
    : "property_type";

  // The legacy surface needs `name` to stay exactly "property_type" (its
  // handleSubmit filters on it) while still giving each of its two lists
  // distinct element ids — they were both emitting id={key}, so clicking a
  // label in More filters toggled the checkbox in the Property type dropdown.
  const elementIdPrefix = idPrefix || fieldName;

  const checked = Array.isArray(selectedValues)
    ? selectedValues
    : filterData?.property_type || [];

  const handleCheckboxChange =
    onChange ||
    ((event) => {
      const { value, checked: isChecked } = event.target;
      const updatedValues = isChecked
        ? [...checked, value]
        : checked.filter((v) => v !== value);

      setValue(fieldName, updatedValues);
      // If you want to update other fields too:
      setValue("property_type", updatedValues);
    });

  const { residential, commercial, hasHiddenCommercial } = groupPropertyTypes(
    propertyCatsCounts,
    memData
  );

  const renderOption = ([key, count]) => {
    const isSelected = checked.includes(key);

    return (
      <div className="col-md-6" key={key}>
        <div className="lbl_btn">
          <input
            type="checkbox"
            name={fieldName}
            id={`${elementIdPrefix}_${key}`}
            value={key}
            {...(uncontrolled
              ? { defaultChecked: isSelected }
              : { checked: isSelected })}
            onChange={handleCheckboxChange}
          />
          <label htmlFor={`${elementIdPrefix}_${key}`}>
            <span>{key || "Unknown"}</span>
            <span>{count}</span>
          </label>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Residential Section */}
      {residential.length > 0 && (
        <>
          <div
            className="col-md-12"
            key="residential-header"
            style={{ marginBottom: "8px" }}
          >
            <h6 style={{ ...GROUP_HEADING_STYLE, margin: "8px 0 4px 0" }}>
              Residential
            </h6>
          </div>
          {residential.map(renderOption)}
        </>
      )}

      {/* Commercial Section - waits for a known plan that allows it */}
      {commercial.length > 0 && (
        <>
          <div
            className="col-md-12"
            key="commercial-header"
            style={{
              marginTop: "12px",
              marginBottom: "8px",
              borderTop: "1px solid #e0e0e0",
              paddingTop: "12px",
            }}
          >
            <h6 style={{ ...GROUP_HEADING_STYLE, margin: "0 0 4px 0" }}>
              Commercial
            </h6>
          </div>
          {commercial.map(renderOption)}
        </>
      )}

      {/* Notice for restricted users */}
      {hasHiddenCommercial && (
        <div
          className="col-md-12"
          key="restricted-notice"
          style={{
            marginTop: "12px",
            padding: "12px",
            backgroundColor: "#f5f5f5",
            borderRadius: "4px",
            borderLeft: "3px solid #0066cc",
            fontSize: "12px",
            color: "#666",
          }}
        >
          <strong>Commercial properties</strong>{" "}
          {upgradeTargetFor(memData)
            ? `are available with ${upgradeTargetFor(memData)}`
            : "are not included on your plan"}
        </div>
      )}
    </>
  );
}
